import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner, Empty } from "@/components/site/Bits";
import { publicApi, useApi, errMsg, fileUrl, fmtDate, fmtPrice, fmtTime } from "@/lib/api";
import { useMe, usePageTitle } from "@/lib/site";
import { VerifyNotice } from "@/pages/Account";

const Field = ({ id, label, ...p }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} className="h-12 bg-white" data-testid={`enroll-${id}-input`} {...p} />
  </div>
);

export default function Enroll() {
  usePageTitle("التسجيل في الدورة");
  const { id } = useParams();
  const nav = useNavigate();
  const api = useApi();
  const { me, loading } = useMe();
  const [c, setC] = useState(undefined);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);
  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setChecking(true);
    try {
      const r = await api("post", "/coupons/validate", { code: coupon.trim(), course_id: c.id });
      setApplied(r);
      toast.success(`تم تطبيق القسيمة — خصم ${fmtPrice(r.discount)}`);
    } catch (err) {
      setApplied(null);
      toast.error(errMsg(err));
    } finally {
      setChecking(false);
    }
  };
  const removeCoupon = () => {
    setApplied(null);
    setCoupon("");
  };
  useEffect(() => {
    publicApi.get(`/courses/${id}`).then((r) => setC(r.data)).catch(() => setC(null));
  }, [id]);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (touched || !me) return;
    setForm({ full_name: me.name || "", phone: me.phone || "", email: me.email || "" });
  }, [me, touched]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("post", "/enrollments", { course_id: c.id, ...form, coupon_code: applied?.code || "" });
      if (r.status === "confirmed") return nav(`/payment/result?ref=${r.ref}`);
      toast.success(`تم إنشاء طلب التسجيل ${r.reg_no}، جارٍ تحويلك إلى بوابة الدفع...`);
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  if (c === undefined || loading) return <Spinner />;
  if (c === null) return <div className="container-x py-20"><Empty text="الدورة غير موجودة" /></div>;
  const set = (k) => (e) => {
    setTouched(true);
    setForm({ ...form, [k]: e.target.value });
  };
  const closed = c.is_past || !c.registration_open;
  const finalPrice = applied ? applied.final_price : c.price;
  if (me && !me.email_verified)
    return <div className="container-x py-16 max-w-3xl" data-testid="enroll-page"><VerifyNotice testId="enroll-verify-notice" /></div>;
  return (
    <div className="container-x py-12 md:py-16 grid lg:grid-cols-12 gap-8" data-testid="enroll-page">
      <form onSubmit={submit} className="lg:col-span-7 card-lux !transform-none p-7 md:p-9 space-y-5" data-testid="enroll-form">
        <p className="eyebrow">الخطوة 1 من 2 — بيانات المتدرب</p>
        <h1 className="font-display text-3xl font-extrabold">التسجيل في الدورة</h1>
        <Field id="full_name" label="الاسم الكامل" value={form.full_name} onChange={set("full_name")} required minLength={3} />
        <Field id="phone" label="رقم الهاتف" value={form.phone} onChange={set("phone")} required dir="ltr" placeholder="+968 9XXXXXXX" inputMode="tel" />
        <Field id="email" label="البريد الإلكتروني" type="email" value={form.email} onChange={set("email")} required dir="ltr" />
        <div className="space-y-2" data-testid="coupon-box">
          <Label htmlFor="coupon">قسيمة الخصم (اختياري)</Label>
          <div className="flex gap-2">
            <Input id="coupon" dir="ltr" className="h-12 bg-white uppercase" placeholder="COUPON" value={coupon} disabled={!!applied}
              onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
            {applied ? (
              <button type="button" onClick={removeCoupon} className="btn-gold-outline !py-2 !px-5 text-sm whitespace-nowrap" data-testid="coupon-remove-btn">إلغاء</button>
            ) : (
              <button type="button" onClick={applyCoupon} disabled={checking || !coupon.trim()} className="btn-gold-outline !py-2 !px-5 text-sm whitespace-nowrap" data-testid="coupon-apply-btn">{checking ? "جارٍ التحقق..." : "تطبيق"}</button>
            )}
          </div>
          {applied && <p className="text-sm text-emerald-700" data-testid="coupon-applied-msg">تم تطبيق القسيمة {applied.code}: خصم {fmtPrice(applied.discount)}</p>}
        </div>
        <div className="flex items-start gap-3 pt-2">
          <Checkbox id="agree" checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="enroll-agree-checkbox" />
          <Label htmlFor="agree" className="leading-relaxed text-sm font-normal">أوافق على <a href="/terms" target="_blank" className="text-[var(--copper)] underline">شروط الاستخدام</a> و<a href="/refund" target="_blank" className="text-[var(--copper)] underline">سياسة الإلغاء والاسترداد</a></Label>
        </div>
        <button type="submit" disabled={busy || !agree || closed} className="btn-copper w-full !py-4 text-base" data-testid="enroll-submit-btn">
          <CreditCard size={18} /> {busy ? "جارٍ المعالجة..." : closed ? "التسجيل غير متاح" : finalPrice <= 0 ? "تأكيد التسجيل مجاناً" : `المتابعة إلى الدفع — ${fmtPrice(finalPrice)}`}
        </button>
        <p className="text-xs text-[var(--muted-ink)] flex items-center gap-1.5"><Lock size={13} />سيتم تحويلك إلى صفحة الدفع الآمنة (Paymob). لا نخزّن بيانات بطاقتك.</p>
      </form>
      <aside className="lg:col-span-5 card-lux !transform-none overflow-hidden h-fit" data-testid="enroll-summary">
        <img src={fileUrl(c.image) || "/images/course-ai.jpg"} alt="" className="w-full aspect-[16/9] object-cover" />
        <div className="p-7 space-y-3">
          <h2 className="font-display font-bold text-lg leading-snug" data-testid="enroll-course-title">{c.title}</h2>
          <p className="text-sm text-[var(--muted-ink)]">{fmtDate(c.date)} — {fmtTime(c.time)} — {c.duration_label}</p>
          <p className="text-sm text-[var(--muted-ink)]">{c.mode}</p>
          {applied && (
            <div className="border-t border-[var(--line)] pt-4 mt-4 space-y-1 text-sm">
              <div className="flex justify-between"><span>السعر الأصلي</span><span className="line-through text-[var(--muted-ink)]" data-testid="enroll-original-price">{fmtPrice(c.price)}</span></div>
              <div className="flex justify-between text-emerald-700"><span>الخصم ({applied.code})</span><span data-testid="enroll-discount">− {fmtPrice(applied.discount)}</span></div>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-[var(--line)] pt-4 mt-4">
            <span className="font-semibold">السعر النهائي</span>
            <span className="font-display text-2xl font-black gold-text" data-testid="enroll-final-price">{fmtPrice(finalPrice)}</span>
          </div>
          <p className="text-xs text-[var(--muted-ink)]">المقاعد المتبقية: {c.seats_left}</p>
        </div>
      </aside>
    </div>
  );
}
